import { Row } from '@components/Layout/Row';
import { SummaryContext } from '@components/SummarySelection/context';
import { selectCategory } from '@components/SummarySelection/data/selectors';
import { SummaryCard } from '@components/SummarySelection/SummaryCard';
import { SummaryEditPackageDialog } from '@components/SummarySelection/SummaryEditPackageDialog';
import { SummaryItemHolder } from '@components/SummarySelection/SummaryItemHolder';
import Hidden from '@material-ui/core/Hidden';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { PackageType } from '@model/Package';
import { DialogApi } from '@packages/dialog/createDialog';
import { createMemo } from '@packages/react';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import DRCardWrapper from '../DRCardWrapper';
import { SummaryPackagePlaceholder } from './SummaryPackagePlaceholder';

export const SummaryPackages = createMemo<{ showPrices?: boolean; isDR?: boolean }>(
    ({ showPrices, isDR }) => {
        const [state, actions] = useContext(SummaryContext);
        const packageSpecRef = useRef<DialogApi>();
        const [packageSelected, setPackageSelected] = useState(null);
        const selectedPackages = state.get('products');
        const [length, setLength] = useState(0);
        const isItemsSelected = state.get('products').length > 0 || state.get('skus').length > 0;

        const handlePackageSelect = useCallback(
            (pkg: PackageType) => {
                actions.selectPackage(pkg);
                setPackageSelected(pkg);
            },
            [selectedPackages],
        );

        const handlePackageEdit = useCallback(
            (pkg: PackageType, skus) => {
                actions.editPackage({ item: pkg, skus });
                packageSpecRef?.current?.close();
            },
            [selectedPackages],
        );

        useEffect(() => {
            setLength(selectedPackages.length ?? 0);
        }, []);

        useLayoutEffect(() => {
            if (length < selectedPackages.length && packageSelected) {
                packageSpecRef?.current?.open();
            }
            setLength(selectedPackages.length);
        }, [selectedPackages.length]);

        let packages$: any = null;

        if (state.get('loading')) {
            packages$ = (
                <>
                    <SummaryItemHolder>
                        <SummaryPackagePlaceholder />
                    </SummaryItemHolder>
                    <Hidden xsDown>
                        <SummaryItemHolder>
                            <SummaryPackagePlaceholder />
                        </SummaryItemHolder>
                    </Hidden>
                    <Hidden smDown>
                        <SummaryItemHolder>
                            <SummaryPackagePlaceholder />
                        </SummaryItemHolder>
                    </Hidden>
                    <Hidden mdDown>
                        <SummaryItemHolder>
                            <SummaryPackagePlaceholder />
                        </SummaryItemHolder>
                    </Hidden>
                </>
            );
        } else {
            if (!state.getIn(['errors', 'packages'])) {
                if (state.hasIn(['rawData', 'packages', 'items'])) {
                    const packagesList = state.getIn(['rawData', 'packages', 'items']) as PackageType[];
                    packages$ = packagesList.map((item) => {
                        const child = (
                            <SummaryCard
                                selected={!!selectedPackages.find((pkg) => pkg.id === item.id)}
                                variant={'package'}
                                data={item}
                                onClick={handlePackageSelect}
                                showPrices={showPrices}
                                isDR={isDR}
                                key={item.id}
                            />
                        );
                        return isDR ? (
                            <DRCardWrapper isItemSelected={isItemsSelected}>{child}</DRCardWrapper>
                        ) : (
                            <SummaryItemHolder key={item.id}>{child}</SummaryItemHolder>
                        );
                    });

                    if (packages$.length === 0) {
                        return null;
                    }
                }
            } else {
                packages$ = "Packages couldn't be loaded!";
            }
        }

        return (
            <Row layout={'column'}>
                <SummaryEditPackageDialog
                    apiRef={packageSpecRef}
                    item={{ value: packageSelected }}
                    onItemChange={handlePackageEdit}
                />
                {packages$?.length ? (
                    <Row padding={'24px 0px 10px'} align={'center'}>
                        <Typography>{selectCategory(state)} Packages</Typography>
                        <Tooltip title={'Packages contain multiple items at discounted prices.'}>
                            <Icon style={{ marginLeft: 8 }} color={'disabled'}>
                                info
                            </Icon>
                        </Tooltip>
                    </Row>
                ) : null}
                <Row>{packages$}</Row>
            </Row>
        );
    },
    {
        displayName: 'SummaryPackages',
    },
);
